import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { GATE_BLOCKED, checkPublish, downloadZip } from '../services/PublishService';
import { getPublishTargets, getRegistry, publishImages, publishToGit, publishToGitOps } from '../services/PublishTargetsService';
import { CONNECTIONS_PATH } from '../services/GitService';
import { DeployFilesPreview, GitConfirm, GitResult, RegistryConfirm, RegistryRun, repoPath } from './PublishTargets';
import { consoleNextEnabled, consoleWebUrl, sendToConsole, systemUrl } from '../services/ConsoleNextService';
import { PublishLockfilesFailure, lockfilesProgressLine, lockfilesSummary } from './PublishLockfiles';
import useConsoleDelivery, { trackConsoleDelivery } from '../hooks/useConsoleDeliveries';
import { ConsoleDeliveryCard } from './ConsoleDeliveryStatus';
import PublishJourney, { applyJourney, journeySummary } from './PublishJourney';
import {
  cancelRun as cancelQualityRun, getAccessibilityTargets, getGate, getOverview as getQualityOverview, notifyQualityChanged, overrideGate,
  postAccessibilityResults, recordProvenance, snapshotApiContract, startRun as startQualityRun,
} from '../services/QualityService';
import { runAccessibility } from './quality/a11yRunner';
import './PublishDialog.css';
import PublishDeployNote, { deployedServices } from './PublishDeployNote';
import { PublishDeviceBuilds } from './devicebuilds/DeviceBuildEntry';
import { friction, published as reportPublished } from '../analytics/journey';

const slug = (text) => String(text || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'project';
const STEPS = ['Preparing', 'Creating', 'Uploading'];

// 'Lockfiles' first: made or brought up to date in the sandbox and committed before the code is packed (PublishLockfiles).
const CONSOLE_STEPS = ['Lockfiles', 'Packing the code', 'Sending to Console', 'Registering the System'];
export const GATE_POLL_MS = 2000;

// The release gate (Quality): when its last run is missing or older than the files and some check runs "before
// Publish", a `publish` run starts (or the one already going on is awaited) and the gate is read every 2 s until it
// ends. A blocked gate disables the destinations; an owner may publish anyway with a reason (the override). Any error
// of Quality (an older sandbox, no vfs-service) blocks nothing and shows nothing.
const runsBeforePublish = (when) => Object.values(when || {}).some((w) => w && w.publish);
const SHOWN_ITEMS = 3;

/** The Git gate (gate.git: secrets and licences count every service, Deploy on or off), or null when it is the gate. */
export const gitGateOf = (gate) => (gate?.git && Array.isArray(gate.git.rules) ? gate.git : null);

// The gate's failing rules, except `readiness`: its items are the readiness findings the dialog already lists.
const failingRules = (gate) => (gate?.rules || []).filter((r) => r.on !== false && r.status === 'fail' && r.id !== 'readiness');

/**
 * The gate as the server's 409 (`quality_gate_blocked`) lists it, for when the gate read here does not say blocked
 * (the code changed, an override no longer valid, an AI tool's run since the dialog opened).
 */
export const gateFromBlocking = (blocking = []) => ({
  blocked: true,
  failing: blocking.length,
  rules: blocking.map((r) => ({ id: r.id, check: r.check, label: r.label || r.detail, detail: r.detail || r.label, on: true, status: 'fail', ...(r.missing ? { missing: true, gap: typeof r.missing === 'string' ? r.missing : undefined } : {}) })),
});

/**
 * What to fix before publishing: the production-readiness items (GET /publish/check) and, while the gate blocks, its
 * failing rules (the readiness rule is the same items, counted once). `[{ key, text, why?, fix?, where?, blocks }]`.
 */
export function thingsToFix(findings = [], gate = null) {
  const readiness = (findings || []).map((f, i) => ({
    key: `r-${f.id || 'item'}-${f.service || i}`,
    text: f.title || String(f.message || '').replace(/\.$/, ''),
    why: f.why || null,
    fix: f.fix || null,
    where: f.file ? `${f.file}${f.line ? `:${f.line}` : ''}` : (f.service ? `services/${f.service}` : null),
    blocks: Boolean(f.blocks),
  }));
  // A rule failed by a missing check (no tests, no coverage report): said as missing, with how to lift it.
  const rules = gate?.blocked ? failingRules(gate).map((r) => ({ key: `g-${r.id}`, text: r.missing ? `Missing check: ${r.detail || r.label}` : (r.detail || r.label), why: r.description || null, fix: r.missing ? 'A missing check never counts as passing: add it, or waive it in Quality settings with a reason.' : null, where: null, blocks: true })) : [];
  return [...rules.filter((r) => r.blocks), ...readiness.filter((r) => r.blocks), ...readiness.filter((r) => !r.blocks)];
}

// Accessibility runs in this browser (axe-core on the preview); its results go to Quality. Best effort: never blocks.
async function runAccessibilityInBackground(projectId) {
  try {
    const { targets } = await getAccessibilityTargets(projectId);
    if (!targets?.length) { return; }
    const result = await runAccessibility({ targets });
    await postAccessibilityResults(projectId, result);
  } catch {
    // the gate says what it knows without it
  }
}

// After a successful publish, the API contract is kept to compare the next changes with. Best effort.
function snapshotContract(projectId) {
  Promise.resolve().then(() => snapshotApiContract(projectId)).catch(() => {});
}

/** The image digests a Console delivery reports (`images`, or its components'), `[{ name, digest }]`; none when absent. */
export function deliveryImages(result) {
  const list = Array.isArray(result?.images) ? result.images : (result?.components || []).map((c) => ({ name: c.image || c.name, digest: c.digest }));
  return list
    .filter((image) => image && typeof image.name === 'string' && typeof image.digest === 'string' && image.name && image.digest)
    .map(({ name, digest }) => ({ name, digest }));
}

// After a successful delivery, Quality records the release's signed build provenance (POST /quality/provenance).
// Fire and forget, like the contract snapshot: it never fails the Publish.
function recordRelease(projectId, destination, { releaseId, images } = {}) {
  const clean = Object.fromEntries(Object.entries(destination).filter(([, value]) => value !== undefined && value !== null && value !== ''));
  Promise.resolve()
    .then(() => recordProvenance(projectId, { destination: clean, ...(releaseId ? { releaseId } : {}), ...(images?.length ? { images } : {}) }))
    .catch(() => {});
}

function useReleaseGate(projectId) {
  const [state, setState] = useState({ status: 'loading', gate: null, running: null, canOverride: false, refusedAt: 0 });
  const live = useRef(true);
  const askedOverride = useRef(false);

  const settle = useCallback((answer) => {
    if (!live.current) { return; }
    const gate = answer?.gate || null;
    const known = typeof answer?.canOverride === 'boolean';
    setState((prev) => ({ ...prev, status: 'ready', gate, running: null, ...(known ? { canOverride: answer.canOverride } : {}) }));
    // Older answers of GET /quality/gate have no canOverride: ask the overview once.
    if (gate?.blocked && !known && !askedOverride.current) {
      askedOverride.current = true;
      getQualityOverview(projectId)
        .then((overview) => { if (live.current) { setState((prev) => ({ ...prev, canOverride: Boolean(overview?.canOverride) })); } })
        .catch(() => {});
    }
  }, [projectId]);

  const start = useCallback(async (gate = null) => {
    setState((prev) => ({ ...prev, status: 'checking', gate: gate || prev.gate }));
    try {
      const answer = await startQualityRun(projectId, { trigger: 'publish' });
      if (answer?.browserChecks?.includes('accessibility')) { runAccessibilityInBackground(projectId); }
    } catch (error) {
      // 409 busy: a run already goes on, wait for it. Anything else: show the gate as it is.
      if (error?.code !== 'busy') {
        try { settle(await getGate(projectId)); } catch { if (live.current) { setState((prev) => ({ ...prev, status: 'off' })); } }
      }
    }
  }, [projectId, settle]);

  useEffect(() => {
    live.current = true;
    askedOverride.current = false;
    getGate(projectId).then((answer) => {
      if (!live.current) { return; }
      const gate = answer?.gate || null;
      if (answer?.running) {
        setState((prev) => ({ ...prev, status: 'checking', gate, running: answer.running }));
      } else if (gate && (gate.neverRan || gate.stale) && runsBeforePublish(answer?.settings?.when)) {
        start(gate);
      } else {
        settle(answer);
      }
    }).catch(() => { if (live.current) { setState((prev) => ({ ...prev, status: 'off' })); } });
    return () => { live.current = false; };
  }, [projectId, start, settle]);

  // While checking: the gate every 2 s, until no run goes on.
  useEffect(() => {
    if (state.status !== 'checking') { return undefined; }
    const timer = setInterval(() => {
      getGate(projectId).then((answer) => {
        if (!live.current) { return; }
        if (answer?.running) {
          setState((prev) => ({ ...prev, running: answer.running }));
        } else {
          notifyQualityChanged(projectId);
          settle(answer);
        }
      }).catch(() => { if (live.current) { setState((prev) => ({ ...prev, status: 'off' })); } });
    }, GATE_POLL_MS);
    return () => clearInterval(timer);
  }, [state.status, projectId, settle]);

  const cancel = async () => {
    try { await cancelQualityRun(projectId); } catch { /* the gate says what is left */ }
    try { settle(await getGate(projectId)); } catch { setState((prev) => ({ ...prev, status: 'off' })); }
  };
  // The server refused Publish (409 quality_gate_blocked): the gate read again, blocked as the server said at least.
  const refused = async (blocking) => {
    const fallback = gateFromBlocking(blocking);
    let answer;
    try { answer = await getGate(projectId); } catch { answer = null; }
    const gate = answer?.gate?.blocked ? answer.gate : { ...(answer?.gate || {}), ...fallback };
    settle({ ...(answer || {}), gate });
    if (live.current) { setState((prev) => ({ ...prev, refusedAt: Date.now() })); }
    notifyQualityChanged(projectId);
  };
  const overridden = (answer) => {
    setState((prev) => ({ ...prev, gate: answer?.gate || { ...prev.gate, blocked: false, override: answer?.override } }));
    notifyQualityChanged(projectId);
  };
  return { ...state, rerun: () => start(), cancel, overridden, refused };
}

function QualityBanner({ quality, findings, onSeeQuality, onClose, onAskAssistant }) {
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [mine, setMine] = useState(null);
  const { status, gate, running, refusedAt } = quality;
  // A refusal by the server wins over an override made here: it was not valid (anymore) for this code.
  useEffect(() => { if (refusedAt) { setMine(null); } }, [refusedAt]);
  const ready = status === 'ready' && Boolean(gate);
  // gate.git (vfs-service quality/view.js, while some service's Deploy is off): the gate of Git and GitOps, where
  // secrets and licences count every service. It can block while the deploy gate passes.
  const git = ready ? gitGateOf(gate) : null;
  const overridden = ready && Boolean(mine || (gate.override && !gate.blocked && gate.failing > 0) || (git?.override && !git.blocked && git.failing > 0));
  const blocked = ready && !overridden && Boolean(gate.blocked);
  const gitOnly = ready && !overridden && !blocked && Boolean(git?.blocked);
  const items = thingsToFix(findings, blocked ? gate : (gitOnly ? { ...gate, blocked: true, rules: git.rules } : null));

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const answer = await overrideGate(quality.projectId, reason.trim());
      setMine({ reason: reason.trim() });
      quality.overridden(answer);
    } catch (err) {
      if (err?.code === 'not_blocked') { setMine({ reason: reason.trim() }); quality.overridden({ gate: { ...gate, blocked: false } }); return; }
      const fieldError = (err?.body?.errors || []).find((e) => e.path === 'reason')?.message;
      setError(err?.code === 'owner_only' ? 'Only an owner of the project can publish past the quality gate.' : (fieldError || err?.body?.error || err?.message || 'Could not save the override'));
    } finally {
      setBusy(false);
    }
  };
  const valid = reason.trim().length >= 10;

  let progress = null;
  if (status === 'checking') {
    const checks = Object.values(running?.checks || {});
    const done = checks.filter((c) => c === 'done').length;
    progress = (
      <div aria-live="polite" className="publish-banner publish-banner-info" data-testid="publish-quality-checking" role="status">
        <span><span aria-hidden className="publish-spin" /> Checking quality…{checks.length ? ` ${done} of ${checks.length} checks done.` : ''}</span>
        <button onClick={quality.cancel} type="button">Cancel</button>
      </div>
    );
  }
  let notice = null;
  if (overridden) {
    const override = gate.override || git?.override;
    const by = mine ? 'you' : (override.by?.name || override.by?.email || 'an owner');
    notice = (
      <div className="publish-banner publish-banner-warn" data-testid="publish-quality-override">
        <span>Published with an override by {by}: {mine ? mine.reason : override.reason}</span>
      </div>
    );
  }
  const n = items.length;
  const fix = n > 0 ? (
    <div className={`publish-banner publish-banner-gate ${blocked || gitOnly ? 'publish-banner-bad' : 'publish-banner-warn'}`} data-testid={blocked ? 'publish-quality-blocked' : (gitOnly ? 'publish-quality-git-blocked' : 'publish-fix')} role={blocked || gitOnly ? 'alert' : undefined}>
      <div className="publish-fix-text">
        <strong>{n} {n === 1 ? 'thing' : 'things'} to fix before publishing{gitOnly ? ' to Git' : ''}</strong>
        {blocked && <span className="publish-fix-sub"> · Publish is blocked until they are fixed.</span>}
        {gitOnly && <span className="publish-fix-sub"> · Git and GitOps are blocked until they are fixed: secrets and licences are checked in every service, Deploy on or off, because the code goes to the Git remote whatever the switch.</span>}
        {blocked && refusedAt > 0 && <span className="publish-fix-sub" data-testid="publish-quality-refused"> The server stopped this publish: the Quality release gate blocks it.</span>}
        <ul className="publish-fix-list">
          {items.slice(0, SHOWN_ITEMS).map((item) => <li key={item.key}>{item.text}</li>)}
          {n > SHOWN_ITEMS && <li className="publish-fix-more">+ {n - SHOWN_ITEMS} more</li>}
        </ul>
      </div>
      <span className="publish-banner-actions">
        {onSeeQuality && <button className="publish-banner-primary" onClick={() => { onClose(); onSeeQuality({ section: 'gate' }); }} type="button">See in Quality</button>}
        <button onClick={() => onAskAssistant(items)} type="button">Fix with assistant</button>
        {(blocked || gitOnly) && <button onClick={quality.rerun} type="button">Run again</button>}
        {(blocked || gitOnly) && quality.canOverride && !asking && <button onClick={() => setAsking(true)} type="button">Publish anyway…</button>}
      </span>
      {(blocked || gitOnly) && asking && (
        <div className="publish-override">
          <label htmlFor="publish-override-reason">Why is it safe to publish now?</label>
          <textarea id="publish-override-reason" maxLength={1000} onChange={(event) => setReason(event.target.value)} rows={3} value={reason} />
          <span className="publish-override-hint">At least 10 characters. It is kept with the release and shown in the project&apos;s Activity.</span>
          {error && <span className="publish-override-error">{error}</span>}
          <span className="publish-banner-actions">
            <button onClick={() => { setAsking(false); setError(null); }} type="button">Cancel</button>
            <button className="publish-override-go" disabled={!valid || busy} onClick={submit} type="button">{busy ? 'Saving…' : 'Publish anyway'}</button>
          </span>
        </div>
      )}
    </div>
  ) : null;
  if (!progress && !notice && !fix) { return null; }
  return <>{progress}{notice}{fix}</>;
}

QualityBanner.propTypes = {
  findings: PropTypes.array,
  onAskAssistant: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSeeQuality: PropTypes.func,
  quality: PropTypes.object.isRequired,
};

// What a destination card says when its answer is missing: an older sandbox (404) or any other error.
const unavailable = (failure) => (failure === 'sandbox_outdated' ? 'Your sandbox runs an older version: update it to publish here.' : 'Not available right now.');

// "Publish": the destinations, one confirm step each. See docs/ANALYSIS-deploy-and-customer-journey.md.
// Git, GitOps and the image registry go straight to vfs-service (PublishTargetsService.js): the pod pushes with the
// person's connected account and records the release; the gateway builds the images. `enableGit` is accepted for
// compatibility and no longer used (the Git card follows GET /publish/targets).
export default function PublishDialog({ projectId, projectName, projectDescription, requestedBy, onClose, onOpenQuality, onOpenApps }) {
  const [step, setStep] = useState('pick'); // pick | confirm | progress | result | zip | delivered | building | files
  const [delivery, setDelivery] = useState(null);
  // The Console delivery's Lockfiles step: its answer once it ran (the progress line, the delivered note).
  const [lockfiles, setLockfiles] = useState(null);
  // The Lockfiles step while the sandbox works on it (its job: which service, how many).
  const [lockfilesJob, setLockfilesJob] = useState(null);
  const consoleOn = consoleNextEnabled();
  // The project's delivery to Console, live: after "Send to Console", and on any later visit (pick step).
  const consoleStatus = useConsoleDelivery(consoleOn ? projectId : null);
  const [check, setCheck] = useState(null);
  const [checkError, setCheckError] = useState(null);
  const [target, setTarget] = useState(null);
  const [name, setName] = useState(slug(projectName));
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  // GET /publish/targets and the gateway's GET /publish/registry: undefined while loading, `{ failed: code }` on an error.
  const [targets, setTargets] = useState(undefined);
  const [registry, setRegistry] = useState(undefined);
  const [withDeploy, setWithDeploy] = useState(false);
  const [imageRun, setImageRun] = useState(null);
  // The Git / GitOps form as last submitted, so a refused push comes back to it filled in.
  const [gitForms, setGitForms] = useState({});
  // The Console journey (PublishJourney): where the code lives, where the images are built; null until it has loaded.
  const [journey, setJourney] = useState(null);
  const releaseGate = useReleaseGate(projectId);
  const quality = { ...releaseGate, projectId };
  // While the gate is read, the checks run, or the gate is blocked (no override): the destinations wait.
  const qualityHold = quality.status === 'loading' || quality.status === 'checking' || (quality.status === 'ready' && Boolean(quality.gate?.blocked));
  // Git and GitOps also wait while the Git gate blocks (secrets or licences in a service whose Deploy is off).
  const gitHold = qualityHold || (quality.status === 'ready' && Boolean(gitGateOf(quality.gate)?.blocked));

  useEffect(() => {
    let live = true;
    checkPublish(projectId).then((data) => live && setCheck(data)).catch((err) => live && setCheckError(err.message));
    getPublishTargets(projectId).then((data) => live && setTargets(data || {})).catch((err) => live && setTargets({ failed: err?.code || 'error' }));
    getRegistry(projectId).then((data) => live && setRegistry(data || {})).catch((err) => live && setRegistry({ failed: err?.code || 'error' }));
    const onKey = (event) => event.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => { live = false; document.removeEventListener('keydown', onKey); };
  }, [projectId, onClose]);

  const findings = check?.findings || [];
  // Console, the image registry and GitOps deploy only the services whose Deploy is on (Apps); Git and Download all.
  const deployed = deployedServices(check);
  const openApps = onOpenApps ? (service) => { onClose(); onOpenApps(service); } : null;
  const gitOn = Boolean(targets && !targets.failed && targets.git?.enabled !== false);
  const gitAccounts = targets?.git?.accounts || [];
  const gitopsOn = gitOn && targets?.deploy?.gitops !== false;
  const registryOn = Boolean(registry && !registry.failed && registry.configured && registry.builder?.available !== false);

  const askAssistant = (items) => {
    const list = items.map((item) => [
      `- ${item.text}${item.where ? ` (${item.where})` : ''}`,
      item.why ? `  Why: ${item.why}` : null,
      item.fix ? `  Fix: ${item.fix}` : null,
    ].filter(Boolean).join('\n')).join('\n');
    window.dispatchEvent(new CustomEvent('askAIFromLogs', {
      detail: { message: `Make this project ready to publish. Fix these and tell me what you changed:\n${list}` }
    }));
    onClose();
  };

  // The server's release gate refused (409 quality_gate_blocked): back to the destinations, with the gate banner
  // (what to fix, See in Quality, Publish anyway for an owner). True when it was that.
  const gateRefused = (err) => {
    if (err?.code !== GATE_BLOCKED) { return false; }
    setError(null);
    setStep('pick');
    releaseGate.refused(err.blocking || []);
    return true;
  };

  // Git and GitOps: the pod pushes (and records the release in GET /publish/releases); Quality's provenance here.
  const run = async (body, form) => {
    const gitops = target === 'gitops';
    setGitForms((prev) => ({ ...prev, [target]: form }));
    setError(null);
    setStep('progress');
    setProgress(0);
    setWithDeploy(!gitops && body?.include?.deploy !== false);
    try {
      setProgress(1);
      const published = gitops ? await publishToGitOps(projectId, body) : await publishToGit(projectId, body);
      setProgress(2);
      setResult(published);
      reportPublished(projectId, gitops ? 'gitops' : 'git');
      snapshotContract(projectId);
      const url = published?.webUrl || published?.repoUrl;
      recordRelease(projectId, { kind: 'git', target: repoPath(url), url });
      setStep('result');
    } catch (err) {
      friction('publish', true);
      if (gateRefused(err)) { return; }
      setError(err);
      setStep('confirm');
    }
  };

  // Console Next: deliver the project, then open the new System there.
  const deliver = async () => {
    setError(null);
    // Open the tab now, while this is still a click (popup blockers), and point it at the System once it exists.
    const tab = window.open('about:blank', '_blank');
    setStep('progress');
    setProgress(0);
    setLockfiles(null);
    setLockfilesJob(null);
    try {
      // The choices become true first (a connected repository, the token as an account of the person), then the delivery.
      if (journey?.ready) { await applyJourney(projectId, journey.form, deployed, journey.connections); }
      const result = await sendToConsole({
        projectId,
        name: projectName,
        description: projectDescription,
        services: deployed,
        requestedBy,
        flowUrl: typeof window !== 'undefined' ? window.location.href : undefined,
        // The lockfiles are ready (checked, or updated and committed): the code is packed next.
        onStep: (stepName, answer) => {
          if (stepName === 'lockfiles-progress') { setLockfilesJob(answer); }
          if (stepName === 'lockfiles') { setLockfilesJob(null); setLockfiles(answer); setProgress(1); }
        },
      });
      setProgress(4);
      setDelivery(result);
      trackConsoleDelivery(projectId, result);
      reportPublished(projectId, 'console');
      snapshotContract(projectId);
      const url = result.path ? consoleWebUrl(result.path) : systemUrl(result.system);
      recordRelease(projectId, { kind: 'console', target: result.system, url, environment: result.environment }, { releaseId: result.flowReleaseId, images: deliveryImages(result) });
      if (tab) { tab.location.href = url; } else { window.open(url, '_blank'); }
      setStep('delivered');
    } catch (err) {
      if (tab) { tab.close(); }
      friction('publish', true);
      if (gateRefused(err)) { return; }
      setError(err);
      setStep('confirm');
    }
  };

  // Image registry: the gateway starts a run (202), then RegistryRun follows it. It records the release itself.
  const build = async (options) => {
    setError(null);
    try {
      const answer = await publishImages(projectId, options);
      if (!answer?.run?.id) { throw new Error('The build did not start. Try again in a moment.'); }
      setImageRun(answer.run);
      setStep('building');
    } catch (err) {
      friction('publish', true);
      if (gateRefused(err)) { return; }
      setError(err);
    }
  };
  const built = useCallback((finished) => {
    if (finished?.state === 'succeeded') { reportPublished(projectId, 'registry'); snapshotContract(projectId); } else { friction('publish', true); }
  }, [projectId]);

  const zip = async () => {
    setError(null);
    try {
      const saved = await downloadZip(projectId, slug(name));
      reportPublished(projectId, 'zip');
      snapshotContract(projectId);
      recordRelease(projectId, { kind: 'zip', target: `${slug(name)}.zip` }, { releaseId: saved?.releaseId });
      setStep('zip');
    } catch (err) {
      friction('publish', true);
      setError(err);
    }
  };

  const header = (title, sub) => (
    <div className="publish-head">
      <div>
        <h2 id="publish-title">{title}</h2>
        {sub && <p className="publish-sub">{sub}</p>}
      </div>
      <button aria-label="Close" className="publish-x" onClick={onClose} type="button">&times;</button>
    </div>
  );

  let body;
  if (step === 'pick') {
    body = (
      <>
        {header(`Publish “${projectName}”`, 'Choose where it goes.')}
        {checkError && <div className="publish-banner publish-banner-bad" data-testid="publish-check-error"><span>{checkError} Download still works.</span></div>}
        <QualityBanner findings={findings} onAskAssistant={askAssistant} onClose={onClose} onSeeQuality={onOpenQuality || null} quality={quality} />
        {consoleStatus && <ConsoleDeliveryCard compact status={consoleStatus} />}
        <PublishDeployNote check={check} gate={quality.gate} onOpenApps={openApps} />
        <div className="publish-cards">
          {consoleOn ? (
            <button className="publish-card" disabled={!check || qualityHold} onClick={() => { setTarget('console'); setStep('confirm'); }} type="button">
              <b>Mia-Platform Console</b>
              <small>Deliver to Console: a System with its components, built and deployed with Helm.</small>
              <span className="publish-tag publish-tag-ok">Console v16</span>
            </button>
          ) : (
            <button className="publish-card" disabled type="button">
              <b>Mia-Platform Console</b>
              <small>Create a project in your tenant.</small>
              <span className="publish-tag">Coming soon</span>
            </button>
          )}
          <button
            className="publish-card"
            disabled={!gitOn || !check || gitHold}
            onClick={() => { setError(null); setTarget('git'); setStep('confirm'); }}
            type="button"
          >
            <b>Git repository</b>
            {targets?.failed || (targets && !gitOn)
              ? <small>{targets.failed ? unavailable(targets.failed) : 'Publishing to Git is turned off.'}</small>
              : (
                <span className="publish-providers">
                  {['GitHub', 'GitLab', 'Azure DevOps', 'Bitbucket'].map((provider) => <span key={provider}>{provider}</span>)}
                </span>
              )}
            {gitOn && <span className={`publish-tag${gitAccounts.length ? ' publish-tag-ok' : ''}`}>{gitAccounts.length ? 'Connected' : 'Connect an account first'}</span>}
          </button>
          <button
            className="publish-card"
            data-testid="publish-card-registry"
            disabled={!registryOn || !check || qualityHold}
            onClick={() => { setError(null); setTarget('registry'); setStep('confirm'); }}
            type="button"
          >
            <b>Image registry</b>
            {registryOn && <small>Build the images and push them, with the Helm chart, to {[registry.registry?.host, registry.registry?.prefix].filter(Boolean).join('/') || 'your registry'}.</small>}
            {registry && !registry.failed && !registry.configured && <small>Your Flow admin hasn&apos;t set up an image registry yet.</small>}
            {registry?.configured && registry.builder?.available === false && <small>The image builder is not available{registry.builder.reason ? `: ${registry.builder.reason}` : ''}.</small>}
            {registry?.failed && <small>{unavailable(registry.failed)}</small>}
            {registryOn && <span className="publish-tag publish-tag-ok">{registry.registry?.signing ? 'Signed' : 'Ready'}</span>}
          </button>
          <button
            className="publish-card"
            disabled={!gitopsOn || !check || gitHold}
            onClick={() => { setError(null); setTarget('gitops'); setStep('confirm'); }}
            type="button"
          >
            <b>GitOps repository</b>
            <small>{targets?.failed ? unavailable(targets.failed) : 'The Helm chart and one values file per environment. Argo CD or Flux deploys from it.'}</small>
          </button>
          {/* A zip of your own code always works: also while the gate blocks the other destinations, and when the
              check could not be read (the others depend on it). */}
          <button className="publish-card" onClick={() => { setError(null); setTarget('zip'); setStep('confirm'); }} type="button">
            <b>Download</b>
            <small>A zip of the code, with its deployment files.</small>
            <span className="publish-tag">Always works</span>
          </button>
        </div>
        <p className="publish-note">
          Using something else (Vercel, Harness, Backstage, Port…)? Download the zip, then ask the assistant to add what you need.
          {' '}<button className="publish-text-btn" onClick={() => setStep('files')} type="button">Preview deployment files</button>
        </p>
        <PublishDeviceBuilds projectId={projectId} />
      </>
    );
  } else if (step === 'confirm' && (target === 'git' || target === 'gitops' || target === 'registry')) {
    const titles = { git: 'Publish to Git', gitops: 'Publish to a GitOps repository', registry: 'Publish to the image registry' };
    body = (
      <>
        {header(titles[target], 'Review and continue.')}
        {error && (
          <div className="publish-banner publish-banner-bad" data-testid="publish-error">
            <span>{error.message}{error.repoUrl ? ` ${error.repoUrl}` : ''}</span>
            {error.code === 'no_account' && <a className="publish-btn" href={error.connectUrl || CONNECTIONS_PATH} rel="noopener noreferrer" target="_blank">Connect an account</a>}
            <button onClick={() => { setTarget('zip'); setError(null); }} type="button">Download zip instead</button>
          </div>
        )}
        {target === 'registry' ? (
          <RegistryConfirm onBack={() => { setError(null); setStep('pick'); }} onSubmit={build} projectId={projectId} registry={registry} services={deployed} />
        ) : (
          <GitConfirm
            defaultName={slug(projectName)}
            initialForm={gitForms[target]}
            key={target}
            kind={target}
            onBack={() => { setError(null); setStep('pick'); }}
            onSubmit={run}
            services={target === 'gitops' ? deployed : check?.services || []}
            targets={targets}
          />
        )}
      </>
    );
  } else if (step === 'confirm') {
    const isZip = target === 'zip';
    const isConsole = target === 'console';
    // Console gets only the deployed services; the zip every one.
    const included = isConsole ? deployed : (check?.services || []);
    body = (
      <>
        {header(isZip ? 'Download the code' : 'Deliver to Mia-Platform Console', 'Review and continue.')}
        {error && (
          <div className="publish-banner publish-banner-bad">
            <span>{error.message}{error.repoUrl ? ` ${error.repoUrl}` : ''}</span>
            {!isZip && <button onClick={() => { setTarget('zip'); setError(null); }} type="button">Download zip instead</button>}
          </div>
        )}
        {isConsole && error?.lockfiles && <PublishLockfilesFailure lockfiles={error.lockfiles} />}
        {isConsole ? (
          <p className="publish-note">
            Console creates the System “{projectName}” with one component per service (and a PostgreSQL resource if the project uses one),
            writes a Helm chart for each and, for the ones without their own Dockerfile, a Dockerfile, and builds the images.
            A service that already is a published image (<code>docker run envoyproxy/envoy</code>, or a compose file with an <code>image:</code> and no <code>build:</code>)
            is delivered as it is: no Dockerfile, no build. Then create an environment there and deploy.
            The services&apos; variables and scheduled jobs go along; secrets never do: they are listed there as values to provide.
          </p>
        ) : (
          <div className="publish-field">
            <label htmlFor="publish-name">File name</label>
            <input id="publish-name" onChange={(event) => setName(event.target.value)} value={name} />
          </div>
        )}
        <div className="publish-field">
          <span className="publish-label">Services included</span>
          <div className="publish-services">
            {(included.length ? included : ['project']).map((service) => <span key={service}>{service}</span>)}
          </div>
          {isConsole && <PublishDeployNote check={check} onOpenApps={openApps} />}
        </div>
        {isConsole && (
          <PublishJourney onChange={setJourney} projectId={projectId} projectName={consoleStatus?.system || slug(projectName)} services={deployed} />
        )}
        {isConsole && journey?.ready && (
          <p className="publish-note publish-summary" data-testid="publish-summary">
            Summary: the code lives {journeySummary(journey.form).code}; the images are built by {journeySummary(journey.form).build}.
          </p>
        )}
        {isConsole && journey?.problem && <div className="publish-banner publish-banner-warn" data-testid="publish-journey-problem"><span>{journey.problem}</span></div>}
        {isZip && <p className="publish-note">The zip also holds the deployment files: deploy/docker-compose.prod.yml and a Helm chart in deploy/helm.</p>}
        <p className="publish-note">Chat history and brain files are not included.</p>
        <div className="publish-row">
          <button className="publish-btn" onClick={() => { setError(null); setStep('pick'); }} type="button">Back</button>
          <button
            className="publish-btn publish-btn-primary"
            disabled={(isZip && !name.trim()) || (isConsole && Boolean(journey?.problem))}
            onClick={isZip ? zip : deliver}
            type="button"
          >
            {isZip ? 'Download' : 'Send to Console'}
          </button>
        </div>
      </>
    );
  } else if (step === 'progress') {
    body = (
      <>
        {header(target === 'console' ? 'Delivering to Console…' : 'Publishing…')}
        <ul className="publish-steps">
          {(target === 'console' ? CONSOLE_STEPS : STEPS).map((label, index) => {
            const detail = target === 'console' && index === 0 ? lockfilesSummary(lockfiles) || lockfilesProgressLine(lockfilesJob) : null;
            return (
              <li className={index < progress ? 'done' : index === progress ? 'now' : ''} key={label}>
                <span className="dot" />{label}{detail && <small className="publish-step-detail" data-testid="publish-lockfiles-step">{detail}</small>}
              </li>
            );
          })}
        </ul>
      </>
    );
  } else if (step === 'delivered') {
    // The live status, once Console answers for this delivery (an older answer would show the previous version).
    const live = consoleStatus && (!delivery.version || String(consoleStatus.version) === String(delivery.version)) ? consoleStatus : null;
    const reviewing = live?.state === 'review' && live.reviewPath;
    const url = live ? consoleWebUrl(live.openPath || live.projectPath) : delivery.path ? consoleWebUrl(delivery.path) : systemUrl(delivery.system);
    body = (
      <>
        {header('Delivered to Console')}
        <div className="publish-ok"><i>✓</i>{projectName} is the System {delivery.system}, release {delivery.version}</div>
        <div className="publish-services">
          {(delivery.components || []).map((component) => <span key={component.name}>{component.service} · {component.kind}</span>)}
          {(delivery.resources || []).map((resource) => <span key={resource}>{resource}</span>)}
        </div>
        {delivery.skipped?.length > 0 && <p className="publish-note">Not built: {delivery.skipped.join('; ')}</p>}
        {lockfilesSummary(delivery.lockfiles) && <p className="publish-note" data-testid="publish-delivered-lockfiles">Lockfiles: {lockfilesSummary(delivery.lockfiles)}.</p>}
        {delivery.sourceWarnings?.map((warning) => (
          <div className="publish-banner publish-banner-warn" data-testid="publish-source-warning" key={warning.service}>
            <span>{warning.service}: your repository was not updated. {warning.message}</span>
          </div>
        ))}
        {journey?.ready && <p className="publish-note publish-summary" data-testid="publish-delivered-summary">Code {journeySummary(journey.form).code}. Images built by {journeySummary(journey.form).build}.</p>}
        {live
          ? <ConsoleDeliveryCard actions={false} status={live} />
          : <p className="publish-note">Console received it. Its status shows here, on the project Home and in your notifications.</p>}
        <div className="publish-row">
          {live?.state === 'deployed' && live.devUrl && <a className="publish-btn" href={live.devUrl} rel="noopener noreferrer" target="_blank">Open app in dev</a>}
          <button className="publish-btn" onClick={onClose} type="button">Done</button>
          <a className="publish-btn publish-btn-primary" href={url} onClick={onClose} rel="noopener noreferrer" target="_blank">{reviewing ? 'Review in Console' : 'Open in Console'}</a>
        </div>
      </>
    );
  } else if (step === 'building') {
    body = (
      <>
        {header('Publishing the images')}
        <RegistryRun initialRun={imageRun} onBack={() => setStep('confirm')} onClose={onClose} onDone={built} projectId={projectId} />
      </>
    );
  } else if (step === 'files') {
    body = (
      <>
        {header('Deployment files', 'Generated by Flow for this project.')}
        <DeployFilesPreview onBack={() => setStep('pick')} projectId={projectId} />
      </>
    );
  } else if (step === 'zip') {
    body = (
      <>
        {header('Download ready')}
        <p className="publish-note">{slug(name)}.zip includes {check?.services?.join(', ') || 'the project'}. Chat history and brain files are not included.</p>
        <div className="publish-row"><button className="publish-btn publish-btn-primary" onClick={onClose} type="button">Done</button></div>
      </>
    );
  } else {
    body = (
      <>
        {header('Published')}
        <GitResult kind={target === 'gitops' ? 'gitops' : 'git'} onClose={onClose} projectName={projectName} result={result} withDeploy={withDeploy} />
      </>
    );
  }

  return createPortal(
    <div className="publish-scrim" onClick={onClose} role="presentation">
      <div aria-labelledby="publish-title" aria-modal="true" className="publish-dialog" onClick={(event) => event.stopPropagation()} role="dialog">
        {body}
      </div>
    </div>,
    document.body
  );
}

PublishDialog.propTypes = {
  enableGit: PropTypes.bool,
  projectId: PropTypes.string.isRequired,
  projectName: PropTypes.string,
  projectDescription: PropTypes.string,
  requestedBy: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  // (focus) => void: opens the project's Quality tab on `focus` ({ section: 'gate' }) for "See in Quality".
  onOpenQuality: PropTypes.func,
  // (service) => opens Apps on that service (its Deploy switch), from "Change in Apps"; none: no link.
  onOpenApps: PropTypes.func,
};

PublishDialog.defaultProps = { projectName: 'Project', projectDescription: '', requestedBy: '', enableGit: false, onOpenQuality: null, onOpenApps: null };
